import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostListener,
    inject, OnInit,
    output,
    PLATFORM_ID,
    signal,
    ViewChild
} from '@angular/core';
import {CalendarOptions, DateSelectArg, EventApi, EventClickArg} from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import tippy, {followCursor} from 'tippy.js';
import * as dateFns from 'date-fns';
import {FullCalendarComponent, FullCalendarModule} from '@fullcalendar/angular';
import {ActivatedRoute} from '@angular/router';
import {EventTestFacade} from '../services/event-test-facade.service';
import {EventRepository} from '../services/event-repository';
import {AsyncPipe, isPlatformBrowser, NgIf, NgStyle} from '@angular/common';
import {EventFacade} from '../store/event.facade';
import {coalesce, FetchRequest, RestResponse} from '@abydos-angular/core';
import huLocale from '@fullcalendar/core/locales/hu';
import {ButtonDirective} from 'primeng/button';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {MultiSelect} from 'primeng/multiselect';
import {Panel} from 'primeng/panel';
import {Select} from 'primeng/select';
import {PartnerClientFacade} from '../../partner-client/store/partner-client.facade';
import {ActivityFacade} from '../../activity/store/activity.facade';
import {toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {EventVO} from '../store/event.interfaces';
import {PartnerClientVO} from '../../partner-client/store/partner-client.interfaces';
import {ActivityVO} from '../../activity/store/activity.interfaces';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';


@Component({
    selector: 'app-event-calendar',
    imports: [
        FullCalendarModule,
        NgIf,
        ButtonDirective,
        FormsModule,
        InputText,
        MultiSelect,
        Panel,
        Select,
        NgStyle,
        AsyncPipe
    ],
    templateUrl: './event-calendar.component.html',
    standalone: true,
    styleUrl: './event-calendar.component.scss',
    providers: [EventTestFacade, EventRepository]
})
export class EventCalendarComponent implements OnInit{
    public selectItems = this.eventFacade.selectItems;
    @ViewChild('fullcalendar') fullcalendar: FullCalendarComponent | undefined;
    eventSelectionEmitter = output<EventVO>()
    isMobile = false;
    private breakpointObserver = inject(BreakpointObserver);
    private platformId = inject(PLATFORM_ID);
    isBrowser = isPlatformBrowser(this.platformId);
    initialEvents = signal([])
    partnerList = signal<PartnerClientVO[] | undefined>([]) // = toSignal(this.eventFacade.listPartners(), {initialValue: []})
    activityList = signal<ActivityVO[] | undefined>([]) //  = toSignal(this.eventFacade.listServices(), {initialValue: null});
    categoryFilters = toSignal(of([{label: 'Fizikai munka', value :1}, {label: 'Részidő vállalható', value :2}, {'label': 'Hostess munka', value: 3}]))
    public activityFilterClientPartner: PartnerClientVO | null | string = null;
    public activityFilterName: string | undefined;
    public eventFilterCategories: number[] = [];
    public dialogVisible = false;
    beginningOfMonth?: Date;
    endOfMonth?: Date;
    selectedEvent: EventVO | undefined;
    calendarOptions = signal<CalendarOptions>({
        plugins: [
            interactionPlugin,
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
        ],
        headerToolbar: this.getToolbarConfig(),
        initialView: this.isBrowser && window.innerWidth > 600 ? 'dayGridMonth' : 'listMonth',
        // initialEvents: this.INITIAL_EVENTS, // alternatively, use the `events` setting to fetch from a feed
        weekends: true,
        editable: false,
        selectable: true,
        selectMirror: true,
        // height: '1000px',
        dayMaxEvents: true,
        eventClick: this.handleEventClick.bind(this),
        datesSet: this.handleDatesSet.bind(this),
        eventDidMount: (info) => {
            if (info.event._def.extendedProps['numberOfApplicants'] == info.event._def.extendedProps['reserved'] || !this.canApply(info)) {
                info.el.style.opacity = '0.5';        // vizuális szürkítés
                info.el.style.cursor = 'not-allowed';
                info.el.classList.add('fc-event-disabled');
                info.el.style.color = '#bfbfbf';
                // info.el.style.background = '#bfbfbf';
            } else {
                info.el.style.cursor = 'pointer'
            }
            let pressTimer: any;
            const tooltip = tippy(info.el, {
                theme: 'material',
                followCursor: true,
                plugins: [followCursor],
                trigger: 'manual',
                delay: [500, 0],
                allowHTML: true,
                content: '<div class="calendar-tooltip" style="black: white;padding: 1em; border: transparent 1px solid">'
                    + '<div class="m-2"><span class="font-bold">' + info.event.title + '</span></div>'
                    + '<div class="m-2"><span class="font-bold">Helyszín: </span>' + info.event.extendedProps['address'] + '</div>'
                    + '<div class="m-2"><span class="font-bold">Szervező: </span>' + info.event.extendedProps['activity']?.partnerClient.name + '</div>'
                    + '<div class="m-2"><span class="font-bold">Leírás: </span>' + (info.event.extendedProps['activity']?.description || 'Nincs leírás megadva') + '</div>'
                    + '<div class="m-2"><span class="font-bold">Időpont: </span>' + dateFns.format((info.event.start as Date), 'yyyy.MM.dd HH:mm') + '-' + dateFns.format((info.event.end as Date), 'yyyy.MM.dd HH:mm') + '</div>'
                    + '<div class="m-2"><span class="font-bold">Szabad helyek: </span>' + info.event.extendedProps['numberOfApplicants'] + '/' + info.event.extendedProps['reserved'] + '</div>'
                    + '<div>'
            });
            const startPress = () => {
                pressTimer = setTimeout(() => {
                    tooltip.show();
                }, 500);
            };
            const cancelPress = () => {
                clearTimeout(pressTimer);
                tooltip.hide();
            };
            info.el.addEventListener('touchstart', startPress);
            info.el.addEventListener('touchend', cancelPress);
            info.el.addEventListener('touchmove', cancelPress);

            if (window.matchMedia('(pointer: fine)').matches) {
                tooltip.setProps({
                    trigger: 'mouseenter focus',
                    delay: [500, 0]
                });
            }
            /*
            tippy(info.el, {
                theme: 'material',
                followCursor: true,
                plugins: [followCursor],
                delay: [500, 0],
                allowHTML: true,
                content: '<div class="calendar-tooltip" style="black: white;padding: 1em; border: transparent 1px solid">'
                    + '<div class="m-2"><span class="font-bold">' + info.event.title + '</span></div>'
                    + '<div class="m-2"><span class="font-bold">Helyszín: </span>' + info.event.extendedProps['address'] + '</div>'
                    + '<div class="m-2"><span class="font-bold">Szervező: </span>' + info.event.extendedProps['activity']?.partnerClient.name + '</div>'
                    + '<div class="m-2"><span class="font-bold">Leírás: </span>' + (info.event.extendedProps['activity']?.description || 'Nincs leírás megadva') + '</div>'
                    + '<div class="m-2"><span class="font-bold">Időpont: </span>' + dateFns.format((info.event.start as Date), 'yyyy.MM.dd HH:mm') + '-' + dateFns.format((info.event.end as Date), 'yyyy.MM.dd HH:mm') + '</div>'
                    + '<div class="m-2"><span class="font-bold">Szabad helyek: </span>' + info.event.extendedProps['numberOfApplicants'] + '/' + info.event.extendedProps['reserved'] + '</div>'
                    + '<div>'
            });

             */
        },
        buttonText: {
            today:    'Ma',
            month:    'Hónap',
            week:     'Hét',
            day:      'Nap',
            list:     'Lista'
        },
        events: this.initialEvents(), //this.INITIAL_EVENTS,
        // eventsSet: this.handleEvents.bind(this),
        locale: huLocale,
    });
    currentEvents = signal<EventApi[]>([]);

    @HostListener('window:resize')
    onResize() {
        this.calendarOptions.set({...this.calendarOptions(), headerToolbar: this.getToolbarConfig()});
        if (window.innerWidth > 600) {
            this.fullcalendar?.getApi().changeView('dayGridMonth');
        } else {
            this.fullcalendar?.getApi().changeView('listMonth');
        }
    }

    constructor(private changeDetector: ChangeDetectorRef, private route: ActivatedRoute, private eventFacade: EventFacade,
                private partnerClientFacade: PartnerClientFacade, private activityFacade: ActivityFacade) {
        const request = new FetchRequest<any>()
        const eventRequest = new FetchRequest<any>()
        eventRequest.criteria = {
            baseCriteria: [
                {fieldName: 'available', value: true, operator: 'equals'}
            ],
            representation: 'view'
        }
        eventRequest.operationId = 'timetable';
        this.partnerClientFacade.list(request, result => {
            this.partnerList.set(result.getData())
        })
        this.activityFacade.list(request, result => {
            this.activityList.set(result.getData())
            if ( this.isBrowser ) {
                const element = document.getElementById('searcher-start');
                setTimeout(() => element?.scrollIntoView({ behavior: 'smooth' }));
            }
        })
    }

    ngOnInit() {
        this.eventFacade.initSelectItems();
        this.breakpointObserver.observe([Breakpoints.Handset])
            .subscribe(result => {
                this.isMobile = result.matches;
            });
    }

    private getToolbarConfig() {
        if ( this.isBrowser ) {
            if (window.innerWidth > 600) {
                return  {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
                };
            } else {
                return  {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'listMonth,timeGridDay'
                };
            }
        } else {
            return {};
        }
    }

    handleEventClick(clickInfo: EventClickArg) {
        console.log(clickInfo)
        // ha megtelt az esemény, , vagy határidőn belül vagyunk akkor nem indítható hozzá foglalás
        if (clickInfo.event.extendedProps['numberOfApplicants'] == clickInfo.event.extendedProps['reserved'] || !this.canApply(clickInfo)) {
            clickInfo.jsEvent.preventDefault();
            clickInfo.jsEvent.stopPropagation();
            return
        }
        this.selectedEvent = (this.calendarOptions().events as EventVO[])?.filter((event: EventVO) => event.slotId === clickInfo.event._def.extendedProps['slotId'] && event.eventId === clickInfo.event._def.extendedProps['eventId'])[0]
        if (isNotNullOrUndefined(this.selectedEvent)) {
            this.eventSelectionEmitter.emit({...this.selectedEvent!, id: clickInfo.event._def.extendedProps['eventId']})
        }
    }

    handleEvents(events: EventApi[]) {
        this.currentEvents.set(events);
        this.changeDetector.detectChanges(); // workaround for pressionChangedAfterItHasBeenCheckedError
    }

    createRequest() {
        const request = new FetchRequest<any>()
        request.criteria = {
            baseCriteria: [
                {fieldName: 'start', operator: 'dateBetween', min: this.beginningOfMonth, max: this.endOfMonth},
                {fieldName: 'available', operator: 'equals', value: true},
                {fieldName: 'published', operator: 'equals', value: true},
                {
                    _constructor: 'AdvancedCriteria',
                    operator: 'or',
                    criteria: [
                        {fieldName: 'openFrom', operator: 'isNull'},
                        {fieldName: 'openFrom', operator: 'lessOrEqual', value: new Date()},

                    ]
                }
            ],
            representation: 'view'
        }
        request.operationId = 'timetable';
        let returnedList: ActivityVO[] | undefined = this.activityList()
        console.log(typeof this.activityFilterClientPartner)
        if (this.activityFilterClientPartner !== null && this.activityFilterClientPartner !== 'null') {
            request.criteria.baseCriteria.push({fieldName: 'activity.partnerClient.id', value: Number(this.activityFilterClientPartner), operator: 'equals'})
        }
        if (coalesce(this.activityFilterName?.length, 0)! > 0) {
            request.criteria.baseCriteria.push({fieldName: 'activity.name', value: this.activityFilterName, operator: 'iContains'})
        }
        console.log(this.eventFilterCategories)
        if (this.eventFilterCategories?.length > 0) {
            request.criteria.baseCriteria.push()
            returnedList = returnedList?.filter((activity: ActivityVO) => {
                    const categoryIds = activity.categories?.map((category:any) => category.id)
                    // activity.categories?.includes(this.eventFilterCategories[0])
                    return categoryIds?.includes(this.eventFilterCategories[0]);
            });
        }
        this.getEvents(request)
        return;
    }

    getEvents(request: FetchRequest<any>) {
        this.eventFacade.list(request, (result: RestResponse) => {
            if (isNotNullOrUndefined(result.getData())) {
                const test: any = result.getData()?.map(data => { return {...data, endTime: undefined, startTime: undefined, id: data.slotId+data.id, eventId: data.id} })
                this.calendarOptions.set({...this.calendarOptions(), events: test})
            }
            const element = document.getElementById('calendar-start');
            setTimeout(() => element?.scrollIntoView({ behavior: 'smooth' }));
        })
    }

    handleDatesSet(arg: any) {
        this.beginningOfMonth = arg.start;
        this.endOfMonth = arg.end;
        this.createRequest();
    }

    canApply(event: any) {
        return dateFns.differenceInHours(event.event.start, new Date()) > coalesce(event.event?.extendedProps['activity']?.cancelHoursBeforeEvent, 48)
    }

    getFilterCategories(selectItems: any) {
        return selectItems.get("activityLabel") || [];
    }
}
